import React, { useEffect, useMemo, useState } from 'react'
import { Avatar, Button, Select, Spin, Typography } from 'antd'
import { SearchOutlined, UploadOutlined, UserOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'

import useDebounce from '../../../../util/use-debounce'

import IntlMessages from '../../../../util/IntlMessages'

import { IAutocompleteOption, IUserToImportData } from '../types'
import { Platform } from '@vacationtracker/shared/types/core-event'

const { Text } = Typography

interface IUserSearchProps {
  platform: Platform
  notImportedUsers: IUserToImportData[]
  tableRowIds: string[]
  onSelectUser: (userValue: string) => void
  onSearchUsers: (value: string) => Promise<IAutocompleteOption[]>
  onOpenCsvUpload?: () => void
}

const renderUserOption = (name: string, email: string, imageUrl?: string) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <Avatar
      size={24}
      src={imageUrl || undefined}
      icon={!imageUrl ? <UserOutlined /> : undefined}
      style={!imageUrl ? { backgroundColor: '#7f00ff', flexShrink: 0 } : { flexShrink: 0 }}
    />
    <div style={{ minWidth: 0 }}>
      <div style={{ lineHeight: 1.3 }}>{name}</div>
      {email && (
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {email}
          </Text>
        </div>
      )}
    </div>
  </div>
)

const UserSearch = ({ platform, notImportedUsers, tableRowIds, onSelectUser, onSearchUsers, onOpenCsvUpload }: IUserSearchProps): React.ReactElement => {
  const { formatMessage } = useIntl()
  const [searchValue, setSearchValue] = useState('')
  const [autocompleteUsers, setAutocompleteUsers] = useState<IAutocompleteOption[]>([])
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [notUsersFound, setNotUsersFound] = useState(false)

  const debouncedSearchTerm = useDebounce(searchValue, 500)

  // Microsoft: debounced API search
  useEffect(() => {
    if (platform !== 'microsoft') return
    if (!debouncedSearchTerm) {
      setAutocompleteUsers([])
      setNotUsersFound(false)
      return
    }

    const fetchData = async () => {
      setLoadingUsers(true)
      const results = await onSearchUsers(debouncedSearchTerm)
      setAutocompleteUsers(results)
      setLoadingUsers(false)
      if (results.length === 0) {
        setNotUsersFound(true)
      }
    }

    fetchData()
  }, [debouncedSearchTerm])

  // Filter out users already in the table for Slack/Google
  const filteredNotImportedUsers = notImportedUsers.filter((u) => {
    const parsed = JSON.parse(u.value)
    return !tableRowIds.includes(parsed.id)
  })

  // Build Microsoft options with avatars
  const msOptions = useMemo(() => {
    return autocompleteUsers.map((opt) => {
      const parsed = JSON.parse(opt.value)
      return {
        label: renderUserOption(parsed.realName || parsed.name || opt.label, parsed.mail || '', parsed.imageUrl),
        value: opt.value,
        searchLabel: `${parsed.realName || ''} ${parsed.name || ''} ${parsed.mail || ''}`.toLowerCase(),
      }
    })
  }, [autocompleteUsers])

  // Build Slack/Google options with avatars
  const slackGoogleOptions = useMemo(() => {
    return filteredNotImportedUsers
      .sort((a, b) => {
        const nameA = JSON.parse(a.value).realName?.toLowerCase() || ''
        const nameB = JSON.parse(b.value).realName?.toLowerCase() || ''
        return nameA < nameB ? -1 : 1
      })
      .map((user) => {
        const parsed = JSON.parse(user.value)
        const name = parsed.realName || parsed.name
        const email = parsed.mail || ''
        return {
          label: renderUserOption(name, email, parsed.imageUrl),
          value: user.value,
          searchLabel: `${name} ${email} ${parsed.name || ''}`.toLowerCase(),
        }
      })
  }, [filteredNotImportedUsers])

  const selectComponent =
    platform === 'microsoft' ? (
      <Select
        mode="multiple"
        labelInValue
        showSearch
        filterOption={false}
        searchValue={searchValue}
        onSearch={setSearchValue}
        value={[]}
        onSelect={(labeledValue) => {
          onSelectUser((labeledValue as { value: string }).value)
          setSearchValue('')
          setAutocompleteUsers([])
        }}
        loading={loadingUsers}
        style={{ width: '100%' }}
        placeholder={formatMessage({ id: 'importUsers.form.searchMicrosoftUsersPlaceholder' })}
        suffixIcon={loadingUsers ? <Spin size="small" /> : <SearchOutlined />}
        notFoundContent={notUsersFound && !loadingUsers ? <IntlMessages id="importUsers.form.searchUsersNotFound" /> : loadingUsers ? <Spin size="small" /> : ''}
        options={msOptions}
        optionFilterProp="searchLabel"
        dropdownRender={(menu) => (
          <>
            <div style={{ padding: '6px 12px', color: '#8c8c8c', fontSize: 12, borderBottom: '1px solid #f0f0f0' }}>
              <IntlMessages id="importUsers.form.searchMicrosoftPhotosNote" />
            </div>
            {menu}
          </>
        )}
      />
    ) : (
      <Select
        mode="multiple"
        labelInValue
        showSearch
        filterOption={(input, option) => ((option as any)?.searchLabel || '').includes(input.toLowerCase())}
        value={[]}
        onSelect={(labeledValue) => {
          onSelectUser((labeledValue as { value: string }).value)
        }}
        style={{ width: '100%' }}
        placeholder={<IntlMessages id={platform === 'slack' ? 'importUsers.form.searchSlackUsersPlaceholder' : 'importUsers.form.searchGoogleUsersPlaceholder'} />}
        suffixIcon={<SearchOutlined />}
        options={slackGoogleOptions}
      />
    )

  return (
    <div className="import-page-search-row">
      <div style={{ flex: 1, minWidth: 0 }}>{selectComponent}</div>
      {onOpenCsvUpload && (
        <Button icon={<UploadOutlined />} onClick={onOpenCsvUpload} style={{ flexShrink: 0 }}>
          <IntlMessages id="components.importUsersForm.csvUploadButton" />
        </Button>
      )}
    </div>
  )
}

export default UserSearch
